import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@tellescope/ui";
import { cn } from "@tellescope/utils";
import { Check, CircleCheckBig } from "lucide-react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

const INBOX_STATUS_STYLE_MAP: Record<string, string> = {
  Resolved: "bg-green-500 text-green-50",
  "In Progress": "bg-yellow-200 text-yellow-900",
  New: "bg-blue-500 text-blue-50",
};

const INBOX_STATUSES = ["New", "In Progress", "Resolved"] as const;

export interface InboxStatusBadgeProps {
  inboxStatus: string;
  className?: string;
  onResolve?: () => void;
  resolveButtonPlacement?: "left" | "right";
  onStatusChange?: (status: string) => void;
  onDropdownOpenChange?: (open: boolean) => void;
}

export const InboxStatusBadge = ({
  inboxStatus,
  className,
  onResolve,
  resolveButtonPlacement = "left",
  onStatusChange,
  onDropdownOpenChange,
}: InboxStatusBadgeProps) => {
  const { t } = useTranslation("inbox");
  const [open, setOpen] = useState(false);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen);
      onDropdownOpenChange?.(nextOpen);
    },
    [onDropdownOpenChange]
  );

  const badgeElement = (
    <Badge
      className={cn(
        "text-[8pt] h-4 rounded-sm",
        INBOX_STATUS_STYLE_MAP[inboxStatus],
        onStatusChange && "cursor-pointer",
        className
      )}
    >
      {inboxStatus}
    </Badge>
  );

  if (onStatusChange) {
    return (
      <DropdownMenu open={open} onOpenChange={handleOpenChange}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="appearance-none border-none bg-transparent p-0 m-0"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
          >
            {badgeElement}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
          }}
          onCloseAutoFocus={(e) => {
            e.preventDefault();
          }}
        >
          {INBOX_STATUSES.map((status) => (
            <DropdownMenuItem
              key={status}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setOpen(false);
                if (status !== inboxStatus) {
                  onStatusChange(status);
                }
              }}
              className="flex items-center gap-2"
            >
              <Badge className={cn("text-[8pt] h-4 rounded-sm", INBOX_STATUS_STYLE_MAP[status])}>{status}</Badge>
              {status === inboxStatus && <Check className="h-3 w-3" />}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <div className="flex gap-1 items-center">
      {!!onResolve && inboxStatus !== "Resolved" && resolveButtonPlacement === "left" && (
        <Button
          variant="ghost"
          size="sm-icon"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onResolve();
          }}
          className="h-4"
          hoverLabel={t("Mark Resolved")}
        >
          <CircleCheckBig className="text-muted-foreground" />
        </Button>
      )}
      {badgeElement}
      {!!onResolve && inboxStatus !== "Resolved" && resolveButtonPlacement === "right" && (
        <Button
          variant="ghost"
          size="sm-icon"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onResolve();
          }}
          className="h-4"
          hoverLabel={t("Mark Resolved")}
        >
          <CircleCheckBig className="text-muted-foreground" />
        </Button>
      )}
    </div>
  );
};
